// src/pages/outsourcing_management/requisitions/RequisitionListUI.tsx
//
// Dashboard/list page for the internal Requisition feature
// (EE-207, Phase 2 #6/#7/#8). Scope tabs (mine/all) + filter bar + search +
// server-side pagination/sort, on top of the Phase 1 hooks/network layer.
// This is entry point (a) - Outsourcing > Requisitions > New - into the
// (stubbed) create window; see RequisitionCreateModal.
import { useContext, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { Grid, Tab, Tabs } from "@mui/material";
import { SortingState } from "@tanstack/react-table";
import AppStateContext from "contexts/AppStateContext";
import useStateWithSessionStorage from "hooks/UseStateWithSessionStorage";
import DashboardHeaderBar from "components/Dashboard/DashboardHeaderBar";
import {
  useRequisitionList,
  useRequisitionOptions,
} from "hooks/useRequisitions";
import {
  RequisitionListScope,
  RequisitionType,
} from "types/RequisitionTypes";
import RequisitionTable from "./RequisitionTable";
import useListView from "hooks/useListView";
import RequisitionFilterBar, {
  EMPTY_REQUISITION_FILTERS,
  RequisitionFilterState,
} from "./RequisitionFilterBar";
import RequisitionCreateModal from "./RequisitionCreateModal";
import RequisitionDetailDialog from "./RequisitionDetailDialog";

const REQUISITION_VIEWS: readonly RequisitionListScope[] = ["mine", "all"];

type ParamsType = {
  id?: string;
};

const RequisitionListUI = () => {
  const { token } = useContext(AppStateContext);
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams<ParamsType>();
  // /requisitions/:id is the requisition the dialog is open on - the URL is
  // the one record of that, so the dialog needs no state of its own. Only
  // digits count; anything else is just the board.
  const selectedRequisitionId =
    id && /^\d+$/.test(id) ? Number(id) : undefined;

  // Each tab is a ?view= of its own; a bare /requisitions opens the one used
  // last.
  const [scope, setScope] = useListView<RequisitionListScope>(
    "/requisitions",
    REQUISITION_VIEWS,
    "mine",
    "requisitionView"
  );
  const [searchVal, setSearchVal] = useStateWithSessionStorage<string>(
    "",
    "requisitionSearchVal"
  );
  const [filters, setFilters] = useStateWithSessionStorage<RequisitionFilterState>(
    EMPTY_REQUISITION_FILTERS,
    "requisitionFilters"
  );
  const [page, setPage] = useStateWithSessionStorage<number>(0, "requisitionPage");
  const [pageSize, setPageSize] = useStateWithSessionStorage<number>(
    25,
    "requisitionPageSize"
  );
  const [sorting, setSorting] = useState<SortingState>([{ id: "id", desc: true }]);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // /requisitions?requisitionId=123 was the deep link before the id moved
  // into the path. Links to it are already out there - the SO Part List's
  // "active requisition" indicator (#18) among them - so it is answered by
  // rewriting it to the path form rather than by a second way in.
  useEffect(() => {
    const legacyId = new URLSearchParams(location.search).get("requisitionId");
    if (!legacyId) return;
    const parsed = Number(legacyId);
    history.replace(
      Number.isNaN(parsed) ? "/requisitions" : `/requisitions/${parsed}`
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const { data: options } = useRequisitionOptions(token);


  const sortEntry = sorting[0];

  const queryFilters = useMemo(
    () => ({
      scope,
      status: filters.status.length > 0 ? filters.status : undefined,
      typeCode: filters.typeCode || undefined,
      purposeCode: filters.purposeCode || undefined,
      materialTypeCode: filters.materialTypeCode || undefined,
      toolCategoryCode: filters.toolCategoryCode || undefined,
      supplierId: filters.supplierId,
      salesOrderId: filters.salesOrderId ? Number(filters.salesOrderId) : undefined,
      orderItemId: filters.orderItemId ? Number(filters.orderItemId) : undefined,
      quoteId: filters.quoteId ? Number(filters.quoteId) : undefined,
      createdFrom: filters.createdFrom || undefined,
      createdTo: filters.createdTo || undefined,
      search: searchVal || undefined,
      page: page + 1,
      pageSize,
      sortKey: sortEntry?.id ?? "id",
      sortDirection: (sortEntry?.desc === false ? "asc" : "desc") as "asc" | "desc",
    }),
    [scope, filters, searchVal, page, pageSize, sortEntry]
  );

  const { data, isFetching, refetch } = useRequisitionList(token, queryFilters);

  const handleScopeChange = (_: React.SyntheticEvent, newScope: RequisitionListScope) => {
    setScope(newScope);
    setPage(0);
  };

  const handleSearchChange = (value: string) => {
    setSearchVal(value);
    setPage(0);
  };

  const handleFiltersChange = (newFilters: RequisitionFilterState) => {
    setFilters(newFilters);
    setPage(0);
  };

  return (
    <>
      <RequisitionCreateModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
      />
      <RequisitionDetailDialog
        open={selectedRequisitionId !== undefined}
        requisitionId={selectedRequisitionId}
        // replace, not push: opening and closing a dialog should not leave a
        // trail of board entries for Back to walk through.
        onClose={() => history.replace(`/requisitions?view=${scope}`)}
      />
      <Grid container>
        <Grid item xs={12}>
          <DashboardHeaderBar
            title="Requisitions"
            search={{
              value: searchVal,
              onChange: handleSearchChange,
              placeholder: "Search requisitions..",
            }}
            action={{
              label: "New Requisition",
              onClick: () => setShowCreateModal(true),
            }}
          />
        </Grid>
        <Grid item xs={12} sx={{ paddingLeft: "1.5rem", paddingTop: "1rem" }}>
          <Tabs
            value={scope}
            onChange={handleScopeChange}
            textColor="primary"
            indicatorColor="primary"
          >
            <Tab label="Mine" value="mine" />
            <Tab label="All" value="all" />
          </Tabs>
        </Grid>
        <Grid item xs={12} sx={{ padding: "1rem 1.5rem 0 1.5rem" }}>
          <RequisitionFilterBar
            filters={filters}
            onChange={handleFiltersChange}
            options={options}
          />
        </Grid>
        <Grid item xs={12} sx={{ padding: "1.5rem" }}>
          <RequisitionTable
            requisitions={data?.items ?? []}
            total={data?.total ?? 0}
            isFetching={isFetching}
            page={page}
            pageSize={pageSize}
            sorting={sorting}
            onSortingChange={setSorting}
            onPageChange={setPage}
            onPageSizeChange={(newPageSize) => {
              setPageSize(newPageSize);
              setPage(0);
            }}
            onRowClick={(requisition: RequisitionType) =>
              history.push(`/requisitions/${requisition.id}`)
            }
            onRefresh={() => refetch()}
          />
        </Grid>
      </Grid>
    </>
  );
};

export default RequisitionListUI;
